import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DataTable, DataTableColumn, DataTableCell, Input, Icon } from '@salesforce/design-system-react';
import CQConfirmationModal from 'components/cq-confirmation-modal';
import { IOrg } from 'interfaces/IOrg.interface';
import { PushPortalServiceProvider } from 'services/push-portal.service';
import { normalizeTagNames, validateTagNames, parseTagNames } from 'common/tag.utils';
import { CQAppConstant } from 'app-constants';

/**
 * Props for OrgInfoModal.
 */
interface OrgInfoModalProps {
    /**
     * Org metadata.
     */
    org: IOrg;
    /**
     * Installed managed packages, keyed by package name.
     */
    installedPackages: Record<string, string> | null;
    /**
     * Whether the modal is open.
     */
    isOpen: boolean;
    /**
     * Callback to close the modal.
     */
    onClose: () => void;
    /**
     * Callback when tags are updated.
     */
    onTagsUpdated?: () => void;
}

/**
 * Modal that shows a summary of org metadata and installed managed packages.
 */
function OrgInfoModal({ org, installedPackages, isOpen, onClose, onTagsUpdated }: OrgInfoModalProps) {
    const { t } = useTranslation();
    // Normalize installed packages into renderable key/value pairs.
    const packageEntries = installedPackages ? Object.entries(installedPackages) : [];
    const [tagNames, setTagNames] = useState(org.tagNames || '');
    const [tagError, setTagError] = useState('');
    const [saveLoading, setSaveLoading] = useState(false);
    const [isEditingTags, setIsEditingTags] = useState(false);

    useEffect(() => {
        setTagNames(org.tagNames || '');
        setTagError('');
        setIsEditingTags(false);
    }, [org.orgId, org.tagNames]);

    /**
     * Reset tag edits and close the modal.
     */
    function handleCloseModal() {
        setTagNames(org.tagNames || '');
        setTagError('');
        setIsEditingTags(false);
        onClose();
    }

    /**
     * Validate and persist tag updates for the current org.
     */
    async function handleSaveTags() {
        const validation = validateTagNames(tagNames);
        if (!validation.isValid) {
            setTagError(validation.error || 'Invalid tags');
            return;
        }
        setSaveLoading(true);
        setTagError('');
        try {
            await PushPortalServiceProvider.updateOrg(org.orgId, {
                tagNames: validation.normalized,
            });
            onTagsUpdated?.();
            setIsEditingTags(false);
        } catch (err: any) {
            setTagError(err?.response?.data?.error || 'Failed to update tags');
        } finally {
            setSaveLoading(false);
        }
    }

    /**
     * Reset tag edits and exit edit mode without saving.
     */
    function handleCancelTags() {
        setTagNames(org.tagNames || '');
        setTagError('');
        setIsEditingTags(false);
    }

    const tagList = parseTagNames(org.tagNames || '');

    // Render the tags field with view/edit modes - displays badges in view mode and input field in edit mode
    function renderTagsField() {
        return (
            <div className="slds-grid slds-grid_vertical-align-center slds-wrap">
                <div className="slds-m-right_x-small">
                    {!isEditingTags ? (
                        tagList.length > 0 ? (
                            tagList.map(tag => (
                                <span key={tag} className="slds-badge slds-m-right_x-small">
                                    {tag}
                                </span>
                            ))
                        ) : (
                            '-'
                        )
                    ) : (
                        <div className="slds-form-element">
                            <div className="slds-form-element__control">
                                <Input
                                    id="org-info-tags"
                                    value={tagNames}
                                    placeholder="QA, Finance, Pilot"
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTagNames(event.target.value)}
                                    onBlur={(event: React.ChangeEvent<HTMLInputElement>) => setTagNames(normalizeTagNames(event.target.value))}
                                />
                            </div>
                            {tagError && (
                                <div className="slds-text-color_error slds-text-body_small slds-m-top_x-small">
                                    {tagError}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="slds-shrink-none">
                    {!isEditingTags ? (
                        <Button
                            variant="icon"
                            title="Edit tags"
                            onClick={() => setIsEditingTags(true)}
                        >
                            <Icon category="utility" name="edit" size="x-small" />
                        </Button>
                    ) : (
                        <div>
                            <Button
                                label={saveLoading ? t('push_portal_saving') : t('push_portal_save')}
                                variant="brand"
                                onClick={handleSaveTags}
                                disabled={saveLoading}
                            />
                            <Button
                                label={t('push_portal_cancel')}
                                variant="neutral"
                                onClick={handleCancelTags}
                                disabled={saveLoading}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Define organization metadata fields to display in the modal
    const orgFields = [
        { label: CQAppConstant.PUSH_PORTAL_UI.TEXT.ORG_ID_LABEL, value: org.orgId },
        { label: CQAppConstant.PUSH_PORTAL_UI.TEXT.ORG_TYPE_LABEL, value: org.orgType },
        { label: CQAppConstant.PUSH_PORTAL_UI.TEXT.REGISTERED_BY_LABEL, value: org.createdBy },
        { label: CQAppConstant.PUSH_PORTAL_UI.TEXT.REGISTERED_ADMIN_USERNAME_LABEL, value: org.integrationUsername },
        { label: CQAppConstant.PUSH_PORTAL_UI.TEXT.AUTH_STATUS_LABEL, value: org.authStatus },
        { label: CQAppConstant.PUSH_PORTAL_UI.TEXT.LAST_UPGRADE_STATUS_LABEL, value: org.lastUpgradeStatus || '-' },
        { label: CQAppConstant.PUSH_PORTAL_UI.TEXT.LAST_UPGRADED_TIME_LABEL, value: org.lastUpgradedAt || '-' },
        { label: CQAppConstant.PUSH_PORTAL_UI.TEXT.TAGS_LABEL, value: renderTagsField() },
    ];

    return (
        <CQConfirmationModal
            isOpen={isOpen}
            heading={`Org Info — ${org.orgName}`}
            size="medium"
            cancelLabel={t('push_portal_close')}
            onCancel={handleCloseModal}
            message={
                <div className="slds-m-bottom_medium">
                    <dl className="slds-list_horizontal slds-wrap">
                        {orgFields.map(field => (
                            <React.Fragment key={field.label}>
                                <dt className="slds-item_label slds-text-color_weak slds-truncate slds-size_1-of-4">
                                    {field.label}
                                </dt>
                                <dd className="slds-item_detail slds-truncate slds-size_3-of-4">
                                    {field.value}
                                </dd>
                            </React.Fragment>
                        ))}
                    </dl>
                </div>
            }
            supportingDetails={
                <div>
                    <h3 className="slds-text-heading_small slds-m-bottom_small">
                        {CQAppConstant.PUSH_PORTAL_UI.TEXT.INSTALLED_PACKAGES_LABEL}
                        {packageEntries.length > 0 && (
                            <span className="slds-badge slds-m-left_x-small">{packageEntries.length}</span>
                        )}
                    </h3>

                    {packageEntries.length > 0 ? (
                        <DataTable
                            items={packageEntries.map(([name, version]) => ({ name, version }))}
                            id="installed-packages-table"
                            fixedLayout
                            columnBordered
                            noRowHover
                        >
                            <DataTableColumn label="Package Name" property="name" truncate>
                                <DataTableCell>
                                    {(item: any) => item.name}
                                </DataTableCell>
                            </DataTableColumn>
                            <DataTableColumn label="Version" property="version" width="120px">
                                <DataTableCell>
                                    {(item: any) => <span className="slds-badge">{item.version}</span>}
                                </DataTableCell>
                            </DataTableColumn>
                        </DataTable>
                    ) : (
                        <p className="slds-text-body_small slds-text-color_weak">
                            {installedPackages === null
                                ? 'No package data — click "Sync Packages" on the org row to fetch.'
                                : 'No managed packages installed in this org.'}
                        </p>
                    )}
                </div>
            }
        />
    );
}

export default OrgInfoModal;
